import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Formik } from 'formik';
import { useCommunityContext } from 'providers';
import { Fragment, useMemo } from 'react';

import { useCreateContentRestrictionMutation } from 'shared/graphql';
import { useToast } from 'shared/hooks';

import { ContentRestrictionsForm, ContentRestrictionsFormValues } from '../ContentRestrictionForm';

const SAVING_MESSAGE_KEY = 'SAVING_SETTINGS_COMMUNITY_CONTENT_RESTRICTIONS';

interface Props {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;

  /**
   * Callback to close the modal.
   */
  onClose: () => void;
}

export function CreateContentRestrictionModal(props: Props) {
  const { selectedCommunity: community } = useCommunityContext();
  const { showMessage, showSuccess, showError, dismiss } = useToast();

  const [createRestriction] = useCreateContentRestrictionMutation({
    refetchQueries: ['ListCommunityContentRestrictions'],
    awaitRefetchQueries: true,
  });

  const initials = useMemo<ContentRestrictionsFormValues>(() => {
    const audienceAffiliations = [];
    const scopeCreatorAffiliations = [];

    const initials: ContentRestrictionsFormValues = {
      description: '',
      isActive: true,

      audience: {
        affiliations: audienceAffiliations,
      },

      scope: {
        creatorAffiliations: scopeCreatorAffiliations,
      },
    };

    return initials;
  }, []);

  const handleSubmit = async (values: ContentRestrictionsFormValues) => {
    try {
      showMessage(`Saving content restrictions, please stand by...`, {
        id: SAVING_MESSAGE_KEY,
      });

      const audienceAffiliations = values.audience.affiliations?.map(
        affiliation => affiliation.value,
      );

      const scopeCreatorAffiliations = values.scope.creatorAffiliations?.map(
        affiliation => affiliation.value,
      );

      await createRestriction({
        variables: {
          community: community?.id as string,
          description: values.description,
          isActive: values.isActive,

          audience: {
            affiliations: audienceAffiliations,
          },

          scope: {
            creatorAffiliations: scopeCreatorAffiliations,
          },
        },
      });

      showSuccess(`Successfully created restriction "${values.description}"`);
      props.onClose();
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      }
    } finally {
      dismiss(SAVING_MESSAGE_KEY);
    }
  };

  return (
    <Dialog maxWidth="sm" open={props.isOpen} onClose={props.onClose} fullWidth>
      <DialogTitle>Create Content Restriction</DialogTitle>

      <Formik initialValues={initials} onSubmit={handleSubmit}>
        {form => (
          <Fragment>
            <DialogContent dividers>
              <ContentRestrictionsForm />
            </DialogContent>

            <DialogActions>
              <Button variant="outlined" disabled={form.isSubmitting} onClick={props.onClose}>
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={form.isSubmitting || !form.dirty}
                onClick={form.submitForm}
              >
                Save
              </Button>
            </DialogActions>
          </Fragment>
        )}
      </Formik>
    </Dialog>
  );
}
